// frontend/src/components/UpcomingEventCard.jsx

import React from "react";
import AvatarStack from "./AvatarStack";
import { Plus} from "phosphor-react";

const dayTile = (iso) => {
  if (!iso) return { d: "", m: "" };
  const d = new Date(iso);
  return {
    d: String(d.getDate()),
    m: d.toLocaleString("en-US", { month: "short" }).toUpperCase(),
  };
};

export default function UpcomingEventCard({
  event,
  eventsCount = 0,
  pastEventsCount = 0,
  onManage,
  onCreate,
  onViewAll,
  meId,
  me,
  onRefresh,
  onShowRsvp,
  onSeeEvent,
  rsvp,
  getAuthHeaders,
}) {
  const [saving, setSaving] = React.useState(false);
  const [local, setLocal] = React.useState(event);

  const { d, m } = React.useMemo(
    () => dayTile(event?.start_time),
    [event?.start_time]
  );

  const seedGoing = React.useCallback(
    (e) => {
      if (!e) return false;
      if (typeof e.going_by_me === "boolean") return e.going_by_me;

      const myId = me?.id ?? meId;

      if (Array.isArray(e.attendees_preview) && myId != null) {
        return e.attendees_preview.some((u) => String(u.id) === String(myId));
      }

      return false;
    },
    [me?.id, meId]
  );

  const [going, setGoing] = React.useState(seedGoing(event));

  React.useEffect(() => {
    setLocal(event);
    setGoing(seedGoing(event));
  }, [event, seedGoing]);

  const toMapsUrl = (e) => {
    if (!e) return null;
    if (e.location_url) return e.location_url;
    if (e.place_id) {
      return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(
        e.place_id
      )}`;
    }
    if (typeof e.lat === "number" && typeof e.lng === "number") {
      return `https://www.google.com/maps?q=${e.lat},${e.lng}`;
    }
    if (e.location_text) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        e.location_text
      )}`;
    }
    return null;
  };

  const mapsUrl = toMapsUrl(local);

  if (!local) {
    return (
      <div className="bg-white rounded-2xl p-5 pt-3 pb-3 md:pb-3.5 md:pt-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-blue-500 flex items-center gap-2">
            Upcoming Events
          </h3>

          <div className="flex gap-3">
            {onCreate && (
              <button
                type="button"
                onClick={onCreate}
                className="text-sm text-black rounded-full bg-yellow-200 hover:ring-2 hover:ring-yellow-300 overflow-hidden leading-none [min-width:0] [min-height:0] !px-2.5 !py-1 !h-6"
                style={{ lineHeight: 1 }}
              >
                Create
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600">No upcoming events</p>

        {onViewAll && (
          <button
            type="button"
            className="mt-2 text-xs text-blue-700 hover:underline"
            onClick={onViewAll}
          >
            See past events
          </button>
        )}
      </div>
    );
  }

  const more = Math.max(0, (eventsCount || 0) - 1);

  const handleToggleRsvp = async () => {
    if (saving) return;
    if (typeof rsvp !== "function" || typeof getAuthHeaders !== "function") {
      return;
    }

    setSaving(true);
    const nextGoing = !going;
    const ok = await rsvp(local.id, nextGoing, getAuthHeaders);
    setSaving(false);

    if (ok) {
      setGoing(nextGoing);

      setLocal((prev) => {
        const next = { ...(prev || {}) };
        const you = {
          id: me?.id ?? meId ?? -1,
          name: me?.name || "You",
          profile_pic_url: me?.profile_pic_url || null,
        };

        const list = Array.isArray(next.attendees_preview)
          ? [...next.attendees_preview]
          : [];

        if (nextGoing) {
          if (!list.some((u) => String(u.id) === String(you.id))) {
            list.unshift(you);
          }
          next.attendees_count = (next.attendees_count || 0) + 1;
        } else {
          const idx = list.findIndex((u) => String(u.id) === String(you.id));
          if (idx >= 0) list.splice(idx, 1);
          next.attendees_count = Math.max(0, (next.attendees_count || 0) - 1);
        }

        next.attendees_preview = list.slice(0, 10);
        next.going_by_me = nextGoing;

        return next;
      });

      if (typeof onRefresh === "function") {
        try {
          await onRefresh();
        } catch {}
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl px-4 py-3.5 md:pb-3.5 md:pt-3.5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg ml-1 text-blue-600 font-semibold flex items-center gap-2">
          Upcoming Events
        </h3>

        <div className="flex gap-3">
          {onCreate && (
            <button
              type="button"
              onClick={onCreate}
              className="text-sm text-black rounded-full bg-yellow-200 hover:ring-2 hover:ring-yellow-300 overflow-hidden leading-none [min-width:0] [min-height:0] !px-2.5 !py-1 !h-6"
              style={{ lineHeight: 1 }}
            >
              Create
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-start bg-blue-100 rounded-3xl pt-3 pl-3 pr-3 pb-2 gap-5">
        <div className="shrink-0 rounded-2xl bg-white px-3 py-3 pt-2 shadow-[0px_4px_0px_rgba(37,99,235,0.96)] text-center w-14">
          <div className="text-lg font-semibold -mb-1.5 text-amber-500">{d}</div>
          <div className="text-sm font-bold text-gray-500">{m}</div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-600">
            {new Date(local.start_time).toLocaleString("en-US", {
              weekday: "short",
              hour: "numeric",
              minute: "2-digit",
            })}
          </div>

          <div className="text-sm font-medium leading-snug break-words">
            {local.title}
          </div>

          {(local.location_text || mapsUrl) && (
            <div className="mt-1 text-[11.5px] md:text-[11px] text-gray-700 break-words">
              {mapsUrl ? (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {local.location_text || mapsUrl.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <span>{local.location_text}</span>
              )}
            </div>
          )}

          {local.description && (
            <div className="mt-2 text-sm text-gray-700 break-words">
              <div className="line-clamp-4 whitespace-pre-wrap">
                {local.description}
              </div>

              {local.description.length > 140 && (
                <button
                  type="button"
                  onClick={() => onSeeEvent?.(local.id)}
                  className="text-sm font-medium text-blue-700 !min-h-0 !h-5 hover:underline"
                >
                  ... see more
                </button>
              )}
            </div>
          )}

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className={[
                "inline-flex items-center justify-center rounded-full text-[13px] disabled:opacity-50 overflow-hidden leading-none [min-width:0] [min-height:0] !px-3 !py-1.5 !h-7",
                going ? "bg-blue-600 text-white" : "bg-blue-700 text-white",
              ].join(" ")}
              disabled={saving}
              onClick={handleToggleRsvp}
              style={{ lineHeight: 1 }}
            >
              {saving
                ? going
                  ? "Leaving…"
                  : "Joining…"
                : going
                ? "Going!"
                : "Let's do it!"}
            </button>

            <div
              className="cursor-pointer flex items-center justify-center !h-7 overflow-hidden"
              role="button"
              tabIndex={0}
              onClick={onShowRsvp}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && onShowRsvp?.()
              }
              style={{ minHeight: 0, lineHeight: 1 }}
            >
              <AvatarStack users={local.attendees_preview || []} />
            </div>
          </div>

          <div className="mt-2">
            {more > 0 ? (
              <button
                type="button"
                className="inline-flex items-center gap-0.5 text-xs !min-h-0 !h-6 text-blue-700 hover:underline"
                onClick={onViewAll}
              >
                <Plus size={8} weight="bold" />
                {more} more event{more === 1 ? "" : "s"}
              </button>
            ) : (
              onManage && (
                <button
                  type="button"
                  className="text-xs text-blue-800 hover:font-bold"
                  onClick={onManage}
                >
                  Manage Events
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}